import { Link } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableCard, TableCardField, TableCardList } from '@/components/ui/table-card'
import { WithdrawClaimButton } from '@/components/WithdrawClaimButton'
import { cn, formatDate } from '@/lib/utils'
import { ClaimOutcome, ClaimStatus } from '@/models/claim'
import { useCurrentUser } from '@/models/user'

export const CLAIM_STATUS_LABELS: Record<ClaimStatus, string> = {
  [ClaimStatus.DRAFT]: 'Draft',
  [ClaimStatus.SUBMITTED]: 'Submitted',
  [ClaimStatus.IN_REVIEW]: 'In review',
  [ClaimStatus.INFO_NEEDED]: 'Info needed',
  [ClaimStatus.FILED]: 'Filed to carrier',
  [ClaimStatus.OPEN_WITH_CARRIER]: 'Open with carrier',
  [ClaimStatus.CLOSED]: 'Closed',
}

export const CLAIM_OUTCOME_LABELS: Record<ClaimOutcome, string> = {
  [ClaimOutcome.PAID]: 'Paid',
  [ClaimOutcome.DENIED]: 'Denied',
  [ClaimOutcome.CLOSED_NO_PAYMENT]: 'No payment',
  [ClaimOutcome.WITHDRAWN]: 'Withdrawn',
}

export const CLAIM_STATUS_STYLES: Record<ClaimStatus, string> = {
  [ClaimStatus.DRAFT]: 'bg-neutral-100 text-neutral-600',
  [ClaimStatus.SUBMITTED]: 'bg-blue-50 text-blue-700',
  [ClaimStatus.IN_REVIEW]: 'bg-gray-100 text-gray-700',
  [ClaimStatus.INFO_NEEDED]: 'bg-orange-50 text-orange-600',
  [ClaimStatus.FILED]: 'bg-gray-100 text-gray-700',
  [ClaimStatus.OPEN_WITH_CARRIER]: 'bg-blue-50 text-blue-700',
  [ClaimStatus.CLOSED]: 'bg-green-50 text-green-700',
}

export interface ClaimRow {
  id: string
  idShort: string
  status: ClaimStatus
  outcome?: ClaimOutcome | null
  dateCreated: string
  dateUpdated?: string
  orgId?: string
  orgName?: string
}

interface ClaimsTableProps {
  claims: ClaimRow[]
  isLoading?: boolean
  showCustomer?: boolean
  emptyMessage?: string
  /** When provided, each row gets View / Edit links built from this base href. */
  getEditHref?: (claim: ClaimRow) => string
  /** Staff-only lifecycle links, rendered after the View / Edit links. */
  rowActions?: (claim: ClaimRow) => React.ReactNode
  /** Replaces the status pill with a control, moved to the table's last column. */
  renderStatus?: (claim: ClaimRow) => React.ReactNode
  /** Offer a way to pull a submitted claim back. */
  allowWithdraw?: boolean
  /** Staff acting for a customer: scopes the withdraw write to their org. */
  withdrawOrgId?: string
}

/** Append the read-only view flag to a claim href, respecting an existing query. */
function withView(href: string): string {
  return `${href}${href.includes('?') ? '&' : '?'}view=1`
}

/** Copy the public photo-upload link for a claim (the page a driver uses to upload). */
async function copyPhotosUrl(claimId: string): Promise<void> {
  const url = `${window.location.origin}/claims/${claimId}/photos`
  try {
    await navigator.clipboard.writeText(url)
    toast.success('Photo upload link copied')
  } catch {
    toast.error(`Copy failed — link: ${url}`)
  }
}

export function ClaimsTable({
  claims,
  isLoading = false,
  showCustomer = false,
  emptyMessage = 'No claims yet.',
  getEditHref,
  rowActions,
  renderStatus,
  allowWithdraw = false,
  withdrawOrgId,
}: ClaimsTableProps) {
  const showActions = !!getEditHref || !!rowActions || allowWithdraw
  const colSpan = (showCustomer ? 4 : 3) + (showActions ? 1 : 0)
  const { user } = useCurrentUser({ enabled: true })
  const isCaiAdmin = user?.isCaiAdmin ?? false

  /** The per-claim links, shared by the row's actions cell and the card's footer. */
  function claimActions(claim: ClaimRow) {
    if (!showActions) return null
    return (
      <>
        {getEditHref && isCaiAdmin && (
          <Button
            variant="link"
            size="sm"
            className="h-auto px-0"
            onClick={() => void copyPhotosUrl(claim.id)}
          >
            Copy photos URL
          </Button>
        )}
        {getEditHref && (isCaiAdmin || claim.status !== ClaimStatus.DRAFT) && (
          <Button variant="link" size="sm" className="h-auto px-0" asChild>
            <Link to={withView(getEditHref(claim))}>View</Link>
          </Button>
        )}
        {getEditHref && (isCaiAdmin || claim.status === ClaimStatus.DRAFT) && (
          <Button variant="link" size="sm" className="h-auto px-0" asChild>
            <Link to={getEditHref(claim)}>Edit</Link>
          </Button>
        )}
        {allowWithdraw && claim.status !== ClaimStatus.DRAFT && claim.status !== ClaimStatus.CLOSED && (
          <WithdrawClaimButton claimId={claim.id} idShort={claim.idShort} orgId={withdrawOrgId} />
        )}
        {rowActions?.(claim)}
      </>
    )
  }

  function statusPill(claim: ClaimRow) {
    return (
      <span
        className={cn(
          'inline-block rounded-full px-2.5 py-0.5 text-xs font-medium',
          CLAIM_STATUS_STYLES[claim.status]
        )}
      >
        {CLAIM_STATUS_LABELS[claim.status]}
        {claim.outcome ? ` · ${CLAIM_OUTCOME_LABELS[claim.outcome]}` : ''}
      </span>
    )
  }

  return (
    <>
      {/* The columns don't fit a phone, so under `lg` each claim becomes a card. */}
      <div className="lg:hidden">
        {!isLoading && claims.length === 0 ? (
          <div className="text-muted-foreground py-6 text-center">{emptyMessage}</div>
        ) : (
          <TableCardList>
            {claims.map((claim) => (
              <TableCard key={claim.id} title={claim.idShort}>
                {showCustomer && (
                  <TableCardField
                    label="Customer"
                    value={
                      claim.orgId ? (
                        <Link to={`/insure/internal/customers/${claim.orgId}`} className="hover:underline">
                          {claim.orgName}
                        </Link>
                      ) : (
                        claim.orgName
                      )
                    }
                  />
                )}
                <TableCardField label="Created" value={formatDate(claim.dateCreated)} />
                <TableCardField label="Status" value={statusPill(claim)} />
                {/* A footer row, not the card's header actions — three links don't fit beside the title. */}
                {(showActions || renderStatus) && (
                  <div className="flex flex-wrap gap-3 pt-1">
                    {claimActions(claim)}
                    {renderStatus?.(claim)}
                  </div>
                )}
              </TableCard>
            ))}
          </TableCardList>
        )}
      </div>
      <div className="max-lg:hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">Claim ID</TableHead>
              {showCustomer && <TableHead className="font-semibold">Customer</TableHead>}
              <TableHead className="font-semibold">Created</TableHead>
              {!renderStatus && <TableHead className="font-semibold">Status</TableHead>}
              {showActions && <TableHead className="font-semibold" />}
              {renderStatus && <TableHead className="font-semibold">Status</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!isLoading && claims.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-muted-foreground py-6 text-center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
            {claims.map((claim) => (
              <TableRow key={claim.id}>
                <TableCell className="font-medium">{claim.idShort}</TableCell>
                {showCustomer && (
                  <TableCell>
                    {claim.orgId ? (
                      <Link to={`/insure/internal/customers/${claim.orgId}`} className="hover:underline">
                        {claim.orgName}
                      </Link>
                    ) : (
                      (claim.orgName ?? '-')
                    )}
                  </TableCell>
                )}
                <TableCell>{formatDate(claim.dateCreated)}</TableCell>
                {!renderStatus && <TableCell>{statusPill(claim)}</TableCell>}
                {showActions && (
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-x-3 gap-y-1">{claimActions(claim)}</div>
                  </TableCell>
                )}
                {renderStatus && <TableCell className="w-px">{renderStatus(claim)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  )
}
